import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Zap, Calendar, BellRing, MapPin, ImagePlus, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import LocationPicker from '../components/LocationPicker';
import { resolveAssetUrl } from '../lib/assets';
import { GIG_CATEGORIES } from '../lib/gigMockData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CreateGigPage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { user } = useAuth();
  const tr = (lv, en, ru) => (language === 'lv' ? lv : language === 'ru' ? ru : en);
  const fileRef = useRef(null);

  const [form, setForm] = useState({
    title: '', description: '', category: '', categoryOther: '', budget: '',
    address: user?.address || '', schedule: 'asap', scheduled_at: '',
  });
  const [loc, setLoc] = useState({
    name: user?.location_name || '',
    region: user?.location_region_slug || user?.region || '',
    lat: user?.lat ?? null, lng: user?.lng ?? null, id: null,
  });
  const [radius, setRadius] = useState([10]);
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Prefill location + address from profile once the user object resolves.
  useEffect(() => {
    if (!user) return;
    setForm((f) => (f.address ? f : { ...f, address: user.address || '' }));
    setLoc((l) => (l.name ? l : {
      name: user.location_name || '',
      region: user.location_region_slug || user.region || '',
      lat: user.lat ?? null, lng: user.lng ?? null, id: null,
    }));
  }, [user]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const room = 5 - images.length;
    if (room <= 0) { toast.error(tr('Maksimums 5 foto', 'Max 5 photos', 'Максимум 5 фото')); return; }
    setUploading(true);
    try {
      for (const file of files.slice(0, room)) {
        const fd = new FormData();
        fd.append('file', file);
        const { data } = await axios.post(`${API}/upload`, fd, { withCredentials: true });
        setImages((prev) => (prev.length < 5 ? [...prev, data.url] : prev));
      }
    } catch (err) {
      toast.error(tr('Augšupielāde neizdevās', 'Upload failed', 'Ошибка загрузки'));
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) { navigate('/login'); return; }
    if (!form.title || !form.category || !form.budget) {
      toast.error(tr('Aizpildi obligātos laukus', 'Fill required fields', 'Заполните обязательные поля'));
      return;
    }
    if (!loc.region) {
      toast.error(tr('Izvēlies atrašanās vietu', 'Pick a location', 'Выберите местоположение'));
      return;
    }
    if (form.category === 'other' && !form.categoryOther.trim()) {
      toast.error(tr('Ieraksti kategoriju', 'Enter the category', 'Укажите категорию'));
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        title: form.title, description: form.description || null,
        category: form.category, category_other: form.category === 'other' ? form.categoryOther : null,
        budget: parseFloat(form.budget), region: loc.region, city: loc.name || null,
        location_id: loc.id, location_name: loc.name || null,
        lat: loc.lat, lng: loc.lng, address: form.address || null,
        radius_km: radius[0], images,
        schedule: form.schedule,
        scheduled_at: form.schedule === 'scheduled' && form.scheduled_at ? new Date(form.scheduled_at).toISOString() : null,
      };
      const { data } = await axios.post(`${API}/gigs`, body, { withCredentials: true });
      toast.success(tr('Haltūra izveidota! Depozīts aizturēts.', 'Gig posted! Escrow held.', 'Создано!'));
      navigate(`/gigs/${data.id}`);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : tr('Neizdevās izveidot', 'Failed to post', 'Ошибка'));
      if (err?.response?.status === 401) navigate('/login');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="create-gig-page">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button onClick={() => navigate('/gigs')} className="flex items-center gap-1 text-[#5C5C5C] hover:text-[#D97736] mb-6 text-sm" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> {tr('Atpakaļ', 'Back', 'Назад')}
        </button>
        <div className="flex items-center gap-2 text-[#D97736] font-semibold mb-2">
          <Zap className="w-5 h-5" /><span className="uppercase tracking-wider text-sm">{tr('Jauna haltūra', 'New gig', 'Новая халтура')}</span>
        </div>
        <h1 className="text-3xl font-bold font-outfit text-[#121212] mb-6">{tr('Publicē darbu un atrodi palīgu', 'Post a gig and find help', 'Опубликуй работу')}</h1>

        <form onSubmit={handleSubmit} className="space-y-5 bg-white border border-[#E5E4E0] rounded-xl p-6">
          <div>
            <Label htmlFor="title">{tr('Nosaukums', 'Title', 'Название')} *</Label>
            <Input id="title" value={form.title} onChange={(e) => set('title', e.target.value)}
              placeholder={tr('Piem. Palīdzība mēbeļu pārvietošanā', 'e.g. Help moving furniture', 'Напр. Помощь с переездом')} data-testid="gig-title-input" />
          </div>
          <div>
            <Label htmlFor="description">{tr('Apraksts', 'Description', 'Описание')}</Label>
            <Textarea id="description" value={form.description} onChange={(e) => set('description', e.target.value)} rows={4} data-testid="gig-description-input" />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>{tr('Kategorija', 'Category', 'Категория')} *</Label>
              <Select value={form.category} onValueChange={(v) => set('category', v)}>
                <SelectTrigger data-testid="gig-category-select"><SelectValue placeholder={tr('Izvēlies', 'Select', 'Выбрать')} /></SelectTrigger>
                <SelectContent>
                  {Object.entries(GIG_CATEGORIES).map(([key, c]) => <SelectItem key={key} value={key}>{c[language] || c.lv}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="budget">{tr('Atlīdzība (€)', 'Budget (€)', 'Оплата (€)')} *</Label>
              <Input id="budget" type="number" min="1" value={form.budget} onChange={(e) => set('budget', e.target.value)} placeholder="40" data-testid="gig-budget-input" />
            </div>
          </div>

          {form.category === 'other' && (
            <div data-testid="category-other-wrap">
              <Label>{tr('Ieraksti kategoriju', 'Specify category', 'Укажите категорию')} *</Label>
              <Input value={form.categoryOther} onChange={(e) => set('categoryOther', e.target.value)}
                placeholder={tr('Piem. Mūzikas ieraksts', 'e.g. Music recording', 'Напр. Запись музыки')} data-testid="gig-category-other-input" />
            </div>
          )}

          {/* Location from the large DB (prefilled from profile, editable) */}
          <div>
            <Label className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {tr('Atrašanās vieta', 'Location', 'Местоположение')} *</Label>
            <LocationPicker value={loc.name}
              onSelect={(l) => setLoc({ name: l.name, region: l.region_slug || loc.region, lat: l.lat, lng: l.lng, id: l.id })}
              placeholder={tr('Meklē pilsētu vai ciemu...', 'Search city or village...', 'Поиск города...')} testid="gig-location-picker" />
            {loc.lat != null && <p className="text-xs text-[#4A6B53] mt-1">✓ {tr('Precīza atrašanās vieta iestatīta', 'Precise location set', 'Точное местоположение')}</p>}
          </div>
          <div>
            <Label htmlFor="address">{tr('Adrese', 'Address', 'Адрес')}</Label>
            <Input id="address" value={form.address} onChange={(e) => set('address', e.target.value)}
              placeholder={tr('Piem. Brīvības iela 1', 'e.g. Brivibas street 1', 'Напр. ул. Свободы 1')} data-testid="gig-address-input" />
          </div>

          {/* Photos (max 5) */}
          <div>
            <Label>{tr('Foto (līdz 5)', 'Photos (up to 5)', 'Фото (до 5)')}</Label>
            <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={handleUpload} data-testid="gig-file-input" />
            <div className="flex flex-wrap gap-2 mt-1">
              {images.map((url, i) => (
                <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden border border-[#E5E4E0]" data-testid={`gig-image-${i}`}>
                  <img src={resolveAssetUrl(url)} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => setImages((p) => p.filter((_, idx) => idx !== i))}
                    className="absolute top-0.5 right-0.5 bg-white/90 rounded-full p-0.5" data-testid={`gig-image-remove-${i}`}>
                    <X className="w-3 h-3 text-red-600" />
                  </button>
                </div>
              ))}
              {images.length < 5 && (
                <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                  className="w-20 h-20 rounded-lg border border-dashed border-[#E5E4E0] flex items-center justify-center text-[#9CA3AF] hover:border-[#D97736] hover:text-[#D97736]" data-testid="gig-upload-btn">
                  {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImagePlus className="w-5 h-5" />}
                </button>
              )}
            </div>
          </div>

          <div>
            <Label>{tr('Kad?', 'When?', 'Когда?')}</Label>
            <div className="flex gap-2 mt-1">
              <button type="button" onClick={() => set('schedule', 'asap')}
                className={`flex-1 flex items-center justify-center gap-1 py-2 rounded-lg border text-sm font-medium transition-colors ${form.schedule === 'asap' ? 'border-[#D97736] bg-[#D97736]/10 text-[#D97736]' : 'border-[#E5E4E0] text-[#5C5C5C]'}`} data-testid="schedule-asap-btn">
                <Zap className="w-4 h-4" /> {tr('Pēc iespējas ātrāk', 'ASAP', 'Срочно')}
              </button>
              <button type="button" onClick={() => set('schedule', 'scheduled')}
                className={`flex-1 flex items-center justify-center gap-1 py-2 rounded-lg border text-sm font-medium transition-colors ${form.schedule === 'scheduled' ? 'border-[#D97736] bg-[#D97736]/10 text-[#D97736]' : 'border-[#E5E4E0] text-[#5C5C5C]'}`} data-testid="schedule-scheduled-btn">
                <Calendar className="w-4 h-4" /> {tr('Noteiktā laikā', 'Scheduled', 'По расписанию')}
              </button>
            </div>
            {form.schedule === 'scheduled' && (
              <Input type="datetime-local" value={form.scheduled_at} onChange={(e) => set('scheduled_at', e.target.value)} className="mt-2" data-testid="gig-datetime-input" />
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {tr('Paziņojumu rādiuss', 'Notification radius', 'Радиус уведомлений')}</Label>
              <span className="text-sm font-semibold text-[#D97736]">{radius[0]} km</span>
            </div>
            <Slider value={radius} onValueChange={setRadius} min={1} max={50} step={1} data-testid="gig-radius-slider" />
            <div className="flex items-start gap-2 mt-3 text-xs text-[#5C5C5C] bg-[#F3F2EE] rounded-lg p-3">
              <BellRing className="w-4 h-4 text-[#D97736] shrink-0 mt-0.5" />
              {tr(`Strādnieki ${radius[0]} km rādiusā automātiski saņems paziņojumu par šo darbu.`,
                  `Workers within ${radius[0]} km will be automatically notified about this gig.`,
                  `Исполнители в радиусе ${radius[0]} км получат уведомление.`)}
            </div>
          </div>

          <Button type="submit" disabled={submitting} className="w-full bg-[#D97736] hover:bg-[#C06528] text-white" data-testid="submit-gig-btn">
            {submitting ? tr('Publicē...', 'Posting...', 'Публикуем...') : tr('Publicēt haltūru', 'Post gig', 'Опубликовать')}
          </Button>
        </form>
      </div>
    </div>
  );
}
